import React, { useEffect, useState } from 'react';
import { GaugeIcon, ThermometerIcon, CircleDotIcon, PlusCircleIcon, CheckIcon, ArrowUpIcon, ArrowDownIcon, RefreshCwIcon, ZapIcon } from 'lucide-react';
interface Machine {
  id: string;
  name: string;
  rotor: number;
  pumpe: number;
  strom: number;
  druck: number;
  temp: number;
  perlen: number;
  bemerkung: string;
  lastPearlChange?: string;
  pearlTendency: 'up' | 'down';
}
export function GrindingProcess() {
  const INITIAL_PEARL_VALUES = {
    'KD190/Ba': 630,
    'KD190/Ba2': 575,
    'KD190/Ba3': 575,
    'Bühler T4': 1150
  };
  const initialMachines: Machine[] = [{
    id: '1',
    name: 'Perlmühle 1 (KD190/Ba)',
    rotor: 12,
    pumpe: 103,
    strom: 123,
    druck: 0.2,
    temp: 53,
    perlen: INITIAL_PEARL_VALUES['KD190/Ba'],
    bemerkung: '',
    lastPearlChange: '21.04.2025',
    pearlTendency: 'down'
  }, {
    id: '2',
    name: 'Perlmühle 2 (KD190/Ba2)',
    rotor: 12,
    pumpe: 103,
    strom: 127,
    druck: 0.2,
    temp: 55,
    perlen: INITIAL_PEARL_VALUES['KD190/Ba2'],
    bemerkung: '',
    pearlTendency: 'down'
  }, {
    id: '3',
    name: 'Perlmühle 3 (KD190/Ba3)',
    rotor: 12,
    pumpe: 101,
    strom: 124,
    druck: 0.1,
    temp: 56,
    perlen: INITIAL_PEARL_VALUES['KD190/Ba3'],
    bemerkung: '',
    pearlTendency: 'down'
  }, {
    id: '4',
    name: 'Perlmühle 4 (Bühler T4)',
    rotor: 9.5,
    pumpe: 249,
    strom: 98.8,
    druck: 0.33,
    temp: 58.2,
    perlen: INITIAL_PEARL_VALUES['Bühler T4'],
    bemerkung: '',
    pearlTendency: 'down'
  }];
  const betriebsstunden = [{
    name: 'Perlmühle 1',
    hours: 9242,
    diff: 1
  }, {
    name: 'Perlmühle 2',
    hours: 2837,
    diff: 1
  }, {
    name: 'Perlmühle 3',
    hours: 9634,
    diff: 1
  }, {
    name: 'Perlmühle 4',
    hours: 259,
    diff: 1
  }];
  const [machines, setMachines] = useState<Machine[]>(initialMachines);
  const [editingBemerkung, setEditingBemerkung] = useState<string | null>(null);
  const [tempBemerkung, setTempBemerkung] = useState('');
  useEffect(() => {
    const interval = setInterval(() => {
      setMachines(currentMachines => currentMachines.map(machine => {
        const tempChange = Math.random() * 0.4 - 0.2;
        const druckChange = Math.random() * 0.02 - 0.01;
        const stromChange = Math.random() * 0.6 - 0.3;
        const perlenReduction = Math.random() * 0.3;
        const newPerlen = Math.max(0, machine.perlen - perlenReduction);
        const pearlTendency = machine.lastPearlChange ? 'down' : machine.pearlTendency;
        return {
          ...machine,
          temp: Number((machine.temp + tempChange).toFixed(1)),
          druck: Number((machine.druck + druckChange).toFixed(2)),
          strom: Number((machine.strom + stromChange).toFixed(1)),
          perlen: Number(newPerlen.toFixed(1)),
          pearlTendency: pearlTendency
        };
      }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const handleSaveBemerkung = (machineId: string) => {
    setMachines(machines.map(machine => machine.id === machineId ? {
      ...machine,
      bemerkung: tempBemerkung
    } : machine));
    setEditingBemerkung(null);
    setTempBemerkung('');
  };
  const handlePearlChange = (machineId: string) => {
    const now = new Date();
    const formattedDate = now.toLocaleDateString('de-DE');
    setMachines(machines.map(machine => machine.id === machineId ? {
      ...machine,
      perlen: INITIAL_PEARL_VALUES[machine.name.split(' (')[1].replace(')', '')],
      lastPearlChange: formattedDate,
      pearlTendency: 'up'
    } : machine));
  };
  const calculateTotalCurrent = (machines: Machine[]) => {
    return machines.reduce((total, machine) => total + machine.strom, 0).toFixed(1);
  };
  const MachineCard = ({
    machine
  }: {
    machine: Machine & {
      betriebsstunden: number;
      betriebsstundenDiff: number;
    };
  }) => {
    const pearlWearPercentage = machine.lastPearlChange ? Number(((1 - machine.perlen / INITIAL_PEARL_VALUES[machine.name.split(' (')[1].replace(')', '')]) * 100).toFixed(1)) : 2;
    return <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-id="element-380">
        <div className="flex items-center justify-between mb-4" data-id="element-381">
          <div className="space-y-1" data-id="element-382">
            <h4 className="font-medium text-gray-800" data-id="element-383">{machine.name}</h4>
            <div className="flex items-center text-xs text-gray-500" data-id="element-384">
              <span data-id="element-385">{machine.betriebsstunden}h</span>
              <div className="flex items-center text-green-600 bg-green-50 px-1.5 py-0.5 rounded ml-2" data-id="element-386">
                <ArrowUpIcon size={12} data-id="element-387" />
                <span className="ml-0.5" data-id="element-388">+{machine.betriebsstundenDiff}h</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-389">
            <CircleDotIcon size={16} className={machine.temp < 60 ? 'text-green-500' : 'text-red-500'} data-id="element-390" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4" data-id="element-391">
          <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-392">
            <div className="flex items-center text-xs text-gray-500 mb-1" data-id="element-393">
              <GaugeIcon size={12} className="mr-1" data-id="element-394" />
              Rotor
            </div>
            <div className="font-medium" data-id="element-395">{machine.rotor} U/min</div>
          </div>
          <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-396">
            <div className="flex items-center text-xs text-gray-500 mb-1" data-id="element-397">
              <GaugeIcon size={12} className="mr-1" data-id="element-398" />
              Pumpe
            </div>
            <div className="font-medium" data-id="element-399">{machine.pumpe} U/min</div>
          </div>
          <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-400">
            <div className="flex items-center text-xs text-gray-500 mb-1" data-id="element-401">
              <GaugeIcon size={12} className="mr-1" data-id="element-402" />
              Strom
            </div>
            <div className="font-medium" data-id="element-403">{machine.strom} A</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4" data-id="element-404">
          <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-405">
            <div className="flex items-center text-xs text-gray-500 mb-1" data-id="element-406">
              <GaugeIcon size={12} className="mr-1" data-id="element-407" />
              Druck
            </div>
            <div className="font-medium" data-id="element-408">{machine.druck} bar</div>
          </div>
          <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-409">
            <div className="flex items-center text-xs text-gray-500 mb-1" data-id="element-410">
              <ThermometerIcon size={12} className="mr-1" data-id="element-411" />
              Temp
            </div>
            <div className={`font-medium ${machine.temp < 60 ? 'text-green-600' : 'text-red-600'}`} data-id="element-412">
              {machine.temp}°C
            </div>
          </div>
          <div className="p-2 bg-gray-50 rounded border border-gray-200" data-id="element-413">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1" data-id="element-414">
              <div className="flex items-center" data-id="element-415">
                <CircleDotIcon size={12} className="mr-1" data-id="element-416" />
                Perlen
              </div>
              <button onClick={() => handlePearlChange(machine.id)} className="p-1 hover:bg-gray-100 rounded-full transition-colors" title="Perlen tauschen" data-id="element-417">
                <RefreshCwIcon size={12} className="text-[#3498dd]" data-id="element-418" />
              </button>
            </div>
            <div className="flex items-center justify-between" data-id="element-419">
              <span className="font-medium" data-id="element-420">
                {Math.round(machine.perlen)} kg
              </span>
              <div className={`flex items-center ${machine.pearlTendency === 'up' ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'} px-1.5 py-0.5 rounded text-xs`} data-id="element-421">
                {machine.pearlTendency === 'up' ? <>
                    <ArrowUpIcon size={12} data-id="element-422" />
                    <span className="ml-0.5" data-id="element-423">+2%</span>
                  </> : <>
                    <ArrowDownIcon size={12} data-id="element-424" />
                    <span className="ml-0.5" data-id="element-425">-{pearlWearPercentage}%</span>
                  </>}
              </div>
            </div>
            {machine.lastPearlChange && <div className="text-xs text-gray-500 mt-1" data-id="element-426">
                Letzter Tausch: {machine.lastPearlChange}
              </div>}
          </div>
        </div>
        <div className="pt-2 border-t border-gray-200" data-id="element-427">
          {editingBemerkung === machine.id ? <div className="flex gap-2" data-id="element-428">
              <input type="text" value={tempBemerkung} onChange={e => setTempBemerkung(e.target.value)} placeholder="Bemerkung eingeben..." className="flex-1 px-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500" onKeyDown={e => {
            if (e.key === 'Enter') handleSaveBemerkung(machine.id);
          }} data-id="element-429" />
              <button onClick={() => handleSaveBemerkung(machine.id)} className="px-2 py-1.5 text-gray-400 hover:text-gray-600" data-id="element-430">
                <CheckIcon size={14} data-id="element-431" />
              </button>
            </div> : machine.bemerkung ? <div className="flex items-center justify-between group" data-id="element-432">
              <span className="text-sm text-gray-600" data-id="element-433">{machine.bemerkung}</span>
              <button onClick={() => {
            setEditingBemerkung(machine.id);
            setTempBemerkung(machine.bemerkung);
          }} className="opacity-0 group-hover:opacity-100 transition-opacity px-2 py-1 text-gray-400 hover:text-gray-600" data-id="element-434">
                <PlusCircleIcon size={14} data-id="element-435" />
              </button>
            </div> : <button onClick={() => {
          setEditingBemerkung(machine.id);
          setTempBemerkung('');
        }} className="w-full py-2 px-3 border border-dashed border-gray-300 rounded-md text-sm text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center gap-2" data-id="element-436">
              <PlusCircleIcon size={14} data-id="element-437" />
              Bemerkung hinzufügen
            </button>}
        </div>
      </div>;
  };
  return <div className="bg-gray-50 rounded-lg p-6" data-id="element-438">
      <div className="flex items-center gap-2 mb-6" data-id="element-439">
        <GaugeIcon className="text-[#3498dd]" size={20} data-id="element-440" />
        <h3 className="text-lg font-semibold text-gray-800" data-id="element-441">
          Maschinenparameter
        </h3>
      </div>
      <div className="mb-6" data-id="element-442">
        <div className="grid grid-cols-4 gap-4" data-id="element-443">
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-444">
            <div className="flex items-center justify-between" data-id="element-445">
              <div className="flex items-center gap-2" data-id="element-446">
                <div className="p-2 bg-blue-50 rounded" data-id="element-447">
                  <ZapIcon className="text-[#3498dd]" size={24} data-id="element-448" />
                </div>
                <div data-id="element-449">
                  <h4 className="text-sm font-medium text-gray-700" data-id="element-450">
                    Gesamtstromverbrauch
                  </h4>
                  <p className="text-xs text-gray-500" data-id="element-451">Aktueller Verbrauch</p>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-800" data-id="element-452">
                {calculateTotalCurrent(machines)}{' '}
                <span className="text-sm font-normal text-gray-500" data-id="element-453">A</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-454">
            <div className="flex items-center justify-between" data-id="element-455">
              <div className="flex items-center gap-2" data-id="element-456">
                <div className="p-2 bg-blue-50 rounded" data-id="element-457">
                  <GaugeIcon className="text-[#3498dd]" size={24} data-id="element-458" />
                </div>
                <div data-id="element-459">
                  <h4 className="text-sm font-medium text-gray-700" data-id="element-460">
                    Platzhalter 1
                  </h4>
                  <p className="text-xs text-gray-500" data-id="element-461">Beschreibung</p>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-800" data-id="element-462">
                0 <span className="text-sm font-normal text-gray-500" data-id="element-463">-</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-464">
            <div className="flex items-center justify-between" data-id="element-465">
              <div className="flex items-center gap-2" data-id="element-466">
                <div className="p-2 bg-blue-50 rounded" data-id="element-467">
                  <GaugeIcon className="text-[#3498dd]" size={24} data-id="element-468" />
                </div>
                <div data-id="element-469">
                  <h4 className="text-sm font-medium text-gray-700" data-id="element-470">
                    Platzhalter 2
                  </h4>
                  <p className="text-xs text-gray-500" data-id="element-471">Beschreibung</p>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-800" data-id="element-472">
                0 <span className="text-sm font-normal text-gray-500" data-id="element-473">-</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-474">
            <div className="flex items-center justify-between" data-id="element-475">
              <div className="flex items-center gap-2" data-id="element-476">
                <div className="p-2 bg-blue-50 rounded" data-id="element-477">
                  <GaugeIcon className="text-[#3498dd]" size={24} data-id="element-478" />
                </div>
                <div data-id="element-479">
                  <h4 className="text-sm font-medium text-gray-700" data-id="element-480">
                    Platzhalter 3
                  </h4>
                  <p className="text-xs text-gray-500" data-id="element-481">Beschreibung</p>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-800" data-id="element-482">
                0 <span className="text-sm font-normal text-gray-500" data-id="element-483">-</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4" data-id="element-484">
        {machines.map((machine, index) => <MachineCard key={machine.id} machine={{
        ...machine,
        betriebsstunden: betriebsstunden[index].hours,
        betriebsstundenDiff: betriebsstunden[index].diff
      }} data-id="element-485" />)}
      </div>
    </div>;
}